import { useEffect, useState } from "react";
import { ArrowRight, Check, ChevronDown, X } from "lucide-react";
import { useLang } from "../App";
import Reveal, { Overline } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm() {
  const { lang, t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [propertyRef, setPropertyRef] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("flen-property-inquiry");
      if (raw) {
        setPropertyRef(JSON.parse(raw));
        setForm((f) => (f.project_type ? f : { ...f, project_type: c.projectOptions[1] }));
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          language: lang,
          property_ref: propertyRef ? `${propertyRef.title} · ${propertyRef.zip} ${propertyRef.city}` : "",
        }),
      });
      if (!res.ok) throw new Error("failed");
      sessionStorage.removeItem("flen-property-inquiry");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const label = "mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50";

  return (
    <section id="contact" data-testid="contact-section" className="bg-ivory px-6 py-28 md:px-12 md:py-40 lg:px-20">
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
        <div>
          <Reveal>
            <Overline>{c.label}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-serif text-4xl font-medium leading-[1.15] text-ink sm:text-5xl lg:text-6xl">
              {c.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/65">{c.subtitle}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-14 space-y-6 border-t border-ink/10 pt-10 text-sm">
              <div className="flex justify-between gap-6">
                <span className="uppercase tracking-[0.25em] text-ink/45 text-[11px]">{c.emailLabel}</span>
                <a data-testid="contact-email-link" href={`mailto:${t.footer.email}`} className="text-ink transition-colors duration-300 hover:text-bronze">
                  {t.footer.email}
                </a>
              </div>
              <div className="flex justify-between gap-6">
                <span className="uppercase tracking-[0.25em] text-ink/45 text-[11px]">{c.locationLabel}</span>
                <span className="text-ink" data-testid="contact-location">{c.locationValue}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {status === "success" ? (
            <div data-testid="contact-success" className="flex h-full flex-col justify-center border border-bronze/40 p-10 md:p-14">
              <span className="flex h-12 w-12 items-center justify-center border border-bronze text-bronze">
                <Check size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-8 font-serif text-3xl font-medium text-ink md:text-4xl">{c.successTitle}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-ink/60">{c.successText}</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-9">
              {propertyRef && (
                <div data-testid="contact-property-ref" className="flex items-center justify-between gap-4 border border-bronze/40 px-4 py-3">
                  <p className="text-xs font-light text-ink/70">
                    <span className="mr-2 text-[10px] font-medium uppercase tracking-[0.2em] text-bronze">
                      {c.propertyInquiry}
                    </span>
                    {propertyRef.title} · {propertyRef.zip} {propertyRef.city}
                  </p>
                  <button
                    type="button"
                    data-testid="contact-property-ref-remove"
                    onClick={() => {
                      setPropertyRef(null);
                      sessionStorage.removeItem("flen-property-inquiry");
                    }}
                    aria-label="Retirer"
                    className="text-ink/40 transition-colors duration-300 hover:text-ink"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <div className="grid gap-9 sm:grid-cols-2">
                <div>
                  <label htmlFor="first_name" className={label}>{c.firstName} *</label>
                  <input id="first_name" data-testid="contact-firstname-input" required className="flen-input"
                    value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                </div>
                <div>
                  <label htmlFor="last_name" className={label}>{c.lastName} *</label>
                  <input id="last_name" data-testid="contact-lastname-input" required className="flen-input"
                    value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </div>
              </div>
              <div className="grid gap-9 sm:grid-cols-2">
                <div>
                  <label htmlFor="email" className={label}>{c.email} *</label>
                  <input id="email" type="email" data-testid="contact-email-input" required className="flen-input"
                    value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div>
                  <label htmlFor="phone" className={label}>{c.phone}</label>
                  <input id="phone" type="tel" data-testid="contact-phone-input" className="flen-input"
                    value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
              </div>
              <div>
                <label htmlFor="project_type" className={label}>{c.project} *</label>
                <div className="relative">
                  <select
                    id="project_type"
                    data-testid="contact-project-select"
                    required
                    value={form.project_type}
                    onChange={(e) => set("project_type", e.target.value)}
                    className="flen-input w-full cursor-pointer appearance-none pr-6"
                  >
                    <option value="" disabled>
                      {c.project}
                    </option>
                    {c.projectOptions.map((opt) => (
                      <option key={opt} value={opt} data-testid={`contact-project-option-${opt}`}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-ink/40" />
                </div>
              </div>
              <div>
                <label htmlFor="message" className={label}>{c.message} *</label>
                <textarea id="message" rows={4} data-testid="contact-message-input" required className="flen-input resize-none"
                  value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>
              {status === "error" && (
                <p data-testid="contact-error" className="text-sm text-red-800">{c.errorText}</p>
              )}
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={status === "sending"}
                className="group flex items-center gap-4 border border-ink/40 px-10 py-5 text-[11px] font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-500 hover:border-bronze hover:bg-bronze hover:text-ivory disabled:opacity-50 md:text-xs"
              >
                {status === "sending" ? c.sending : c.submit}
                <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
